import BottomSheet, {
  BottomSheetBackdrop,
  BottomSheetModal,
  BottomSheetScrollView,
  type BottomSheetBackdropProps,
} from '@gorhom/bottom-sheet';
import { useCallback, useEffect, useRef } from 'react';
import { StyleSheet } from 'react-native';
import { useTheme } from 'react-native-paper';

interface AppBottomSheetProps {
  readonly visible: boolean;
  readonly snapPoints?: (string | number)[];
  readonly index?: number;
  readonly onDismiss: () => void;
  readonly onChange?: (index: number) => void;
  readonly children: React.ReactNode;
}

const styles = StyleSheet.create({
  handle: {
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
  },
  indicator: {
    width: 40,
    height: 4,
  },
});

export const AppBottomSheet = ({
  visible,
  snapPoints = ['50%', '90%'],
  index = 0,
  onDismiss,
  onChange,
  children,
}: AppBottomSheetProps) => {
  const theme = useTheme();
  const ref = useRef<BottomSheetModal>(null);

  useEffect(() => {
    if (visible) {
      ref.current?.present();
      return;
    }
    ref.current?.dismiss();
  }, [visible]);

  const renderBackdrop = useCallback(
    (props: BottomSheetBackdropProps) => (
      <BottomSheetBackdrop
        {...props}
        disappearsOnIndex={-1}
        appearsOnIndex={0}
        pressBehavior="close"
      />
    ),
    [],
  );

  return (
    <BottomSheetModal
      ref={ref}
      index={index}
      snapPoints={snapPoints}
      enableDynamicSizing={false}
      enablePanDownToClose
      backdropComponent={renderBackdrop}
      onDismiss={onDismiss}
      onChange={onChange}
      handleStyle={[styles.handle, { backgroundColor: theme.colors.surface }]}
      handleIndicatorStyle={[styles.indicator, { backgroundColor: theme.colors.outlineVariant }]}
      backgroundStyle={{ backgroundColor: theme.colors.surface }}>
      {children}
    </BottomSheetModal>
  );
};

export { BottomSheetScrollView, BottomSheet };
